import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import lang from '../../../lib/language';
import { TradingPlan } from '../../../lib/models/crm/types';
import { ReportingCall } from '../../../lib/models/reporting/types';
import { RootState } from '../../../lib/store';
import { getAccountsAvailableCash } from '../../../lib/store/common-actions';
import { getCurrentAccountTradingPlan } from '../../../lib/store/crm/helpers';
import { getAccountTradeHistoryFirstAndLastTrade } from '../../../lib/store/reporting';
import {
  GetAccountTradeHistoryFirstAndLastTradePayload,
  GetAllAvailableCashPayload,
} from '../../../lib/store/reporting/types';
import { formatCurrency } from '../../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';

import USAFlag from '../../../assets/img/united-states-of-america.svg';
import WarningIcon from '../../../assets/img/warning-icon.svg';
import { CONSISTENCY_RULE_WARNING } from '../../../containers/Dashboard/components/ScoreCalculator/ScoreCalculator.constant';
import { useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import { UI_ROUTES } from '../../../util/routes';
import { ClickableImage, MobileDrawer } from '../..';
import PayoutsSkeletonLoader from '../PayoutsSkeletonLoader/PayoutsSkeletonLoader';

import { useWithdrawButton } from './useWithdrawButton';

import styles from './PayoutsHeader.module.scss';

type OwnProps = {
  currentPlan?: TradingPlan;
  isUserVerified: boolean;
};

type Props = OwnProps & ReduxProps;

const PayoutsHeader = ({
  currentPlan,
  payoutSplit,
  referenceIds,
  availableCash,
  getAvaiablePayout,
  accountStatusData,
  getTradeHistoryFirstAndLastTrade,
  hasOlderTradeThanFourteenDays,
  isReadyFirstAndLastTrade,
  isUserVerified,
}: Props) => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [showProfitSplitTooltip, setShowProfitSplitTooltip] = useState<boolean>(false);
  const { totalRealizedPnl, consistencyScore } = accountStatusData;
  const isLoading = !isReadyFirstAndLastTrade;

  const { isWithdrawButtonEnabled, potentialPayout } = useWithdrawButton({
    currentPlan,
    isGettingFirstAndLastTrade: isLoading,
    availableCash,
    payoutSplit,
    totalRealizedPnl,
    hasOlderTradeThanFourteenDays,
  });

  useEffect(() => {
    if (referenceIds?.length > 0) {
      getAvaiablePayout({ accountReferenceIds: referenceIds });
      if (!hasOlderTradeThanFourteenDays) {
        getTradeHistoryFirstAndLastTrade({ accountsList: referenceIds[0] });
      }
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.potentialPayoutText}>
          <div>{lang.potentialPayout()}</div>
          <div className={styles.iconWithTooltipWrapper} />
        </div>
        <div className={styles.amountContainer}>
          <img src={USAFlag} alt="" />
          {isLoading && isUserVerified ? (
            <PayoutsSkeletonLoader width={100} height={32} />
          ) : (
            <div>{formatCurrency(potentialPayout || '0')}</div>
          )}
          {consistencyScore < CONSISTENCY_RULE_WARNING && (
            <ClickableImage
              onMouseEnter={() => !isMobile && setShowProfitSplitTooltip(true)}
              onMouseLeave={() => !isMobile && setShowProfitSplitTooltip(false)}
              onClick={() => isMobile && setShowProfitSplitTooltip(true)}
              className={styles.icon}
              src={WarningIcon}
              alt=""
            />
          )}
          {showProfitSplitTooltip && !isMobile && (
            <div className={styles.payoutTooltip}>
              <div>{lang.consistencyRuleBelow70()}</div>
              <div>{lang.profitSplitReduced()}</div>
            </div>
          )}
        </div>
      </div>
      <Button
        className={styles.withdrawButton}
        fullWidth={isMobile}
        biggerFontSize
        disabled={!isWithdrawButtonEnabled}
        onClick={() => navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods)}
      >
        {lang.commonWithdraw()}
      </Button>
      {isMobile && (
        <MobileDrawer
          isOpen={showProfitSplitTooltip}
          title={lang.consistencyRuleWarning()}
          onClose={() => setShowProfitSplitTooltip(false)}
        >
          <div className={styles.payoutDrawerText}>
            <div>
              {lang.consistencyRuleBelow70()}
              {' '}
              {lang.profitSplitReduced()}
            </div>
          </div>
        </MobileDrawer>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountStatusData: state.closeout.accountStatusData,
  payoutSplit: getCurrentAccountTradingPlan(state.crm)?.payout_split,
  hasOlderTradeThanFourteenDays: state.reporting.hasOlderTradeThanFourteenDays,
  isReadyFirstAndLastTrade: isCallStatusReady(
    ReportingCall.getAccountTradeHistoryFirstAndLastTrade,
    state.reporting.statusByCall,
  ),
  availableCash: state.payment.availableCashInfo.availableCash.map(
    (available) => available.totalInBaseCurrency.availableCash,
  ),
  referenceIds: state.crm.cashAccounts,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getAvaiablePayout: (payload: GetAllAvailableCashPayload) =>
    dispatch(getAccountsAvailableCash(payload)),
  getTradeHistoryFirstAndLastTrade: (data: GetAccountTradeHistoryFirstAndLastTradePayload) =>
    dispatch(getAccountTradeHistoryFirstAndLastTrade(data)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(PayoutsHeader);
